import { useState, useEffect, useCallback } from 'react'
import GenericTable from '../GenericTable'
import { d1Query, d1Insert, d1Update, d1Delete } from '../../services/d1Service'
import type { Institution, StatusMessage } from '../../types'

interface Props { onStatus: (msg: StatusMessage) => void }

const EMPTY: Partial<Institution> = {
  eiin: '', name_bn: '', name_en: '', established_year: '',
  logo_url: '', banner_url: '', principal_name_bn: '', principal_name_en: '',
  principal_title_bn: '', principal_title_en: '', principal_message_bn: '',
  principal_message_en: '', principal_photo_url: '', photo1: '', photo2: '', photo3: '', photo4: '', photo5: '', map_embed_url: '',
  facebook_page_url: '', phone: '', email: '', address_bn: '', address_en: '', national_anthem_url: ''
}

export default function InstitutionsTab({ onStatus }: Props) {
  const [items, setItems] = useState<Institution[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<Institution> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await d1Query<Institution>('SELECT * FROM institutions ORDER BY id ASC')) }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editing) return
    try {
      if ((editing as any).id) await d1Update('institutions', (editing as any).id, editing)
      else await d1Insert('institutions', editing)
      onStatus({ type: 'success', text: 'Institution saved!' })
      setEditing(null); load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  const handleDelete = async (id: number) => {
    try { await d1Delete('institutions', id); onStatus({ type: 'success', text: 'Deleted!' }); load() }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  return (
    <GenericTable
      title="Institutions" icon="🏛️"
      items={items} loading={loading} editingItem={editing}
      hideAddButton={true}
      columns={[
        { key: 'eiin', label: 'EIIN', width: '90px' },
        { key: 'name_bn', label: 'নাম (BN)', width: '180px' },
        { key: 'name_en', label: 'Name (EN)', width: '200px' },
        { key: 'established_year', label: 'Est. Year', width: '100px' },
        { key: 'phone', label: 'Phone' },
        { key: 'email', label: 'Email' }
      ]}
      formFields={[
        { key: 'eiin', label: 'EIIN', required: true, disabled: !!(editing as any)?.id },
        { key: 'name_bn', label: 'Name (BN)', required: true, disabled: !!(editing as any)?.id },
        { key: 'name_en', label: 'Name (EN)', required: true, disabled: !!(editing as any)?.id },
        { key: 'established_year', label: 'Established Year', required: true, disabled: !!(editing as any)?.id },
        { key: 'phone', label: 'Phone' },
        { key: 'email', label: 'Email' },
        { key: 'address_bn', label: 'Address (BN)' },
        { key: 'address_en', label: 'Address (EN)' },
        { key: 'facebook_page_url', label: 'Facebook Page URL' },
        { key: 'national_anthem_url', label: 'National Anthem URL' },
        { key: 'principal_name_bn', label: 'Principal Name (BN)' },
        { key: 'principal_name_en', label: 'Principal Name (EN)' },
        { key: 'principal_title_bn', label: 'Principal Title (BN)' },
        { key: 'principal_title_en', label: 'Principal Title (EN)' },
        { key: 'logo_url', label: 'Logo Image', type: 'image', folder: 'institutions/logos' },
        { key: 'banner_url', label: 'Banner Image', type: 'image', folder: 'institutions/banners' },
        { key: 'principal_photo_url', label: 'Principal Photo', type: 'image', folder: 'institutions/principals' },
        { key: 'photo1', label: 'Photo 1', type: 'image', folder: 'institutions/photos/photo1', accept: 'image/*', allowPdf: false, hint: '📐 সেরা মাপ: 1200px × 380px বা 1600px × 500px (ওয়াইড ব্যানার ছবি)', cropAspectRatio: 1600 / 500, cropOutputWidth: 1600, cropOutputHeight: 500 },
        { key: 'photo2', label: 'Photo 2', type: 'image', folder: 'institutions/photos/photo2', accept: 'image/*', allowPdf: false, hint: '📐 সেরা মাপ: 1200px × 380px বা 1600px × 500px (ওয়াইড ব্যানার ছবি)', cropAspectRatio: 1600 / 500, cropOutputWidth: 1600, cropOutputHeight: 500 },
        { key: 'photo3', label: 'Photo 3', type: 'image', folder: 'institutions/photos/photo3', accept: 'image/*', allowPdf: false, hint: '📐 সেরা মাপ: 1200px × 380px বা 1600px × 500px (ওয়াইড ব্যানার ছবি)', cropAspectRatio: 1600 / 500, cropOutputWidth: 1600, cropOutputHeight: 500 },
        { key: 'photo4', label: 'Photo 4', type: 'image', folder: 'institutions/photos/photo4', accept: 'image/*', allowPdf: false, hint: '📐 সেরা মাপ: 1200px × 380px বা 1600px × 500px (ওয়াইড ব্যানার ছবি)', cropAspectRatio: 1600 / 500, cropOutputWidth: 1600, cropOutputHeight: 500 },
        { key: 'photo5', label: 'Photo 5', type: 'image', folder: 'institutions/photos/photo5', accept: 'image/*', allowPdf: false, hint: '📐 সেরা মাপ: 1200px × 380px বা 1600px × 500px (ওয়াইড ব্যানার ছবি)', cropAspectRatio: 1600 / 500, cropOutputWidth: 1600, cropOutputHeight: 500 },
        { key: 'principal_message_bn', label: 'Principal Message (BN)', type: 'textarea' },
        { key: 'principal_message_en', label: 'Principal Message (EN)', type: 'textarea' },
        { key: 'map_embed_url', label: 'Map Embed URL', type: 'textarea' }
      ]}
      onAdd={() => setEditing({ ...EMPTY })}
      onEdit={(item) => setEditing({ ...item })}
      onDelete={handleDelete}
      onSave={handleSave}
      onCancel={() => setEditing(null)}
      onFieldChange={(key, val) => setEditing(prev => prev ? { ...prev, [key]: val } : prev)}
    />
  )
}
